import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl } from '../../lib/ep.js';
import { useOwner } from '../context/context.js';
import { get_available_domains, set_domain_promotion } from '../../lib/domain.js';
import { useAccount } from '../context/accountContext.js';

import ActionButton, { MainButton, SecondaryButton, SquaredButton } from '../builder/ActionButton.js';
import ActionLink from '../builder/ActionLink';
import ButtonCollection from '../builder/ButtonCollection.js';
import CopyLink from '../builder/CopyLink';
import FloatingLayer from '../builder/FloatingLayer.js';
import Input from '../builder/Input.js';
import Model from '../builder/Model.js';
import SaveButton from '../builder/SaveButton.js';
import SectionSubtitle from '../builder/SectionSubtitle.js';
import SectionTitle from '../builder/SectionTitle.js';
import Select from '../builder/Select.js';
import Spinner from '../builder/Spinner.js';
import Tooltip from '../builder/Tooltip.js';

import PromoShareLayer from '../promotion/PromoShareLayer';

import { ProfileModule } from '../ui_items/Module.js';
import ModuleHeader from '../ui_items/ModuleHeader.js';
import QRViewer from '../ui_items/QRViewer.js';

import PasswordProtected from './elements/PasswordProtected.js';
import FieldLabel from '../builder/FieldLabel.js';

const Wrapper = (props) => {
	const has_buttons = props?.has_buttons ?? true;
	const enable_close = props?.close ?? false;

	return (
		<FloatingLayer
			_class={{ size:'small' }}
			backButton={props.close}
			enable_close={(enable_close !== false) ? true : false}
		>
			<div class={(!has_buttons) ? 'without_actions' : null}>
				<SectionTitle>{props?.title ?? "Use your own domain"}</SectionTitle>

				<SectionSubtitle>Change the domain linked to the promotion for another domain connected to your account.</SectionSubtitle>

				{props.children}
			</div>
		</FloatingLayer>
	)
};

const ManageDomain = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const default_domain = 'a.cstmapp.com';

	const [ domains, setDomains ] = useState([]);
	const [ actual, setActual ] = useState(props?.actual_domain ?? default_domain);
	const [ loading, setLoading ] = useState(true);

	useEffect(() => {
		get_available_domains(props.account_id, props.organizer_id, (domains) => {
			setDomains(domains);

			setTimeout(() => {
				setLoading(false);
			}, 300);
		})
	}, []);

	const changeDomain = (domain) => {
		setActual(domain);
	};

	const save = (e, cb) => {
		let domain_id = false;
		let accountDomain = false;

		if(actual.is_default == undefined) {
			const dom = domains.find((d) => (d.Accountdomain.domain == actual.domain));

			if(dom != undefined) {
				domain_id = dom.Accountdomain.id;
				accountDomain = dom.Accountdomain;
			}
		}

		set_domain_promotion(domain_id, props.promotion_id);

		if(props.onChange != undefined) {
			props.onChange(accountDomain);
		}

		if(cb != undefined) cb();
		setTimeout(() => {
			props.close();
		}, 1300);
	};

	const selectable_domains = [];
	domains.map((domain) => {
		selectable_domains.push({ domain:domain.Accountdomain.domain });
	});
	selectable_domains.push({ domain:default_domain, is_default:true });

	if(loading) {
		return (
			<Wrapper has_buttons={false}>
				<div class="gui_pos_rel" style={{ minHeight:'150px' }}>
					<Spinner>Loading domains</Spinner>
				</div>
			</Wrapper>
		)
	}

	if(selectable_domains.length < 2) {
		return (
			<Wrapper close={props.close}>
				<ButtonCollection in_floating={true}>
					<ActionButton
						_href={`${window.panel_url}domains`}
					>Manage your custom domains</ActionButton>
				</ButtonCollection>
			</Wrapper>
		)
	}

	return (
		<Wrapper
			title="Change domain"
			close={props.close}
		>
			<ul class="items_with_icon_selector">
				{selectable_domains.map((domain, d) => {
					const selected = (domain.domain == actual.domain || (actual === false && domain.is_default === true)) ? true : false;
					const is_default = domain?.is_default ?? false;

					const color = (selected) ? 'rgba(var(--main-color), 1)' : 'rgba(var(--main-bg-color), 1)';

					const icon = (is_default) ? 'knk-links' : 'knk-networks'

					return (
						<li key={`domain_${d}`} class={(selected) ? 'selected' : null} onClick={() => changeDomain(domain)}>
							<span class={`promo_type_icon gui_circled_icon mainTextIcon ${icon}`}>
								<span class="bg" style={{ backgroundColor:color }} />
							</span>

							<span class="name not_uppercase">
								<strong>{domain.domain}</strong>

								{(is_default) && (
									<span class="extra">[{i18n("Default Easypromos domain")}]</span>
								)}
							</span>
						</li>
					)
				})}
			</ul>

			<ButtonCollection in_floating={true}>
				<SaveButton
					onClick={save}
				>Save changes</SaveButton>

				<SecondaryButton
					_href={`${window.panel_url}domains`}
				>Manage your custom domains</SecondaryButton>
			</ButtonCollection>
		</Wrapper>
	)
};

export const QRLinkCampaignLayer = ({ link, promotion, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const default_model = {
		lc:'',
		utm_source:props?.utm_source ?? '',
		utm_medium:props?.utm_medium ?? '',
		utm_campaign:props?.utm_campaign ?? '',
	};

	const [ display_layer, setDisplayLayer ] = useState(props?.display_layer ?? false);
	const [ downloaded, setDownloaded ] = useState(false);
	const [ model, setModel ] = useState(default_model);

	useEffect(()=>{
		setDisplayLayer(props?.display_layer ?? false)
	},[props.display_layer])

	const openLayer = () => {
		setDownloaded(false);
		setDisplayLayer(true);
	};

	const change = (k, v) => {
		setModel({ ...model, [k]:v.trim() });
		setDownloaded(false);
	};

	const getURLParts = (initial_path = '') => {
		const parts = (initial_path != '') ? [initial_path] : [];
		Object.keys(model).map((k) => {
			if(k == 'lc' && model[k] == '-') return;

			if(model[k] != undefined && model[k] != '') {
				parts.push(`${k}=${encodeURIComponent(model[k].trim())}`);
			}
		});

		return parts;
	};

	const constructPathImage = (initial_path = '') => {
		const parts = getURLParts(initial_path);
		if(parts.length == 0) return '';
		return `${parts.join("_")}`;
	};

	const constructFullURL = (url) => {
		const parts = getURLParts();
		if(parts.length == 0) return '';
		return `${url}?${parts.join("&")}`;
	};

	const closeLayer = () => {
		setDisplayLayer(false);
		if(props.close_layer != undefined)props.close_layer();
	}
	
	const link_with_utms = constructFullURL(link ?? `${window.public_url}p/${promotion.id}`);
	const hide_inline_button = props?.display_layer ?? false;

	const languages = [{ k:'-', v:`${i18n("Automatic")} (${i18n("default")})` }];
	if(props.languages != undefined && props.languages.length > 1) {
		props.languages.map((lang) => {
			languages.push({ k:lang.k, v:lang.v.label });
		});
	}

	return (
		<>
			{(!hide_inline_button) && (
				<ActionLink
					onClick={() => openLayer()}
				>Generate QR</ActionLink>
			)}

			{(display_layer) && (
				<FloatingLayer
					backButton={() => closeLayer()} 
					_class={{ size:'small' }}
				>
					<div class="without_actions">
						<SectionTitle>QR Generator</SectionTitle>

						<QRViewer
							url={link_with_utms}
							size={190}
							level='H'
							id='content_qr_link'
							filename={`${constructPathImage('qr')}_${promotion.id}`}
							onFinish={() => setDisplayLayer(false)}
							//enable_copy={false}
						/>

						<div class="gui_not_in_top" style={{ minHeight:'5em' }}>
							<span class="link_display">{link_with_utms}</span>
						</div>

						<div class="gui_not_in_top">
							{(languages.length > 1) && (
								<Model model={model} change={change}>
									<Select
										name='lc'
										values={languages}
										_class={{ first:true, size:60 }}
									>Select language</Select>
								</Model>
							)}

							<div class="gui_not_in_top_big">
								<FieldLabel
									_extra_component={<ActionLink _href={`${window.zendesk_base_url}207885366`} _target="_blank">More information</ActionLink>}
								>UTM parameters</FieldLabel>

								<Model classname='gui_flexbox_auto gui_padded_children_low' model={model} change={change} style={{flexGrow:'1'}}>
									<Input
										name="utm_source"
										_class={{ other:'utm_source campaign_param', first:true }}
									>utm_source</Input>

									<Input
										name="utm_medium"
										_class={{ other:'utm_medium campaign_param', first:true }}
									>utm_medium</Input>

									<Input
										name="utm_campaign"
										_class={{ other:'utm_campaign campaign_param', first:true }}
									>utm_campaign</Input>
								</Model>
							</div>
						</div>
					</div>
				</FloatingLayer>
			)}
		</>
	)
}

const MainPromoLink = (props) => {
	const [ display_link_options, setDisplayLinkOptions ] = useState(false);
	const [ display_password_layer, setDisplayPasswordLayer ] = useState(false);
	const [ manage_domain, setManageDomain ] = useState(false);
	const [ domain, setDomain ] = useState(props?.domain ?? false);

	const owner = useOwner();
	const { MyAccount } = useAccount();

	const promotion = owner?.promotion?.Promotion ?? false;

	const languages = props?.languages ?? [];

	const protocol = `${window.location.href.split("/")[0]}//`;
	const main_promo_link = `${(domain) ? `${protocol}${domain.domain}/` : window.public_url}p/${promotion.id}`;

	const has_custom_domains_enabled = (MyAccount.hasMinPlan(5)) ? true : false;

	return (
		<ProfileModule>
			<ModuleHeader>Link to promotion</ModuleHeader>
			
			<div class="promo_main_link gui_flexbox_auto" style={{ gap:'1.5em' }}>
				<ActionLink
					_href={main_promo_link}
					_target="_blank"
					autotranslate={false}
				>{main_promo_link}</ActionLink>

				<ButtonCollection>
					{(domain && domain.status != '1') && (
						<SquaredButton
							icon='knk-alert whiteIcon redButton'
							tooltip={<Tooltip with_icon={false} position='top'>The connected domain is not active. Please, review your connected domains.</Tooltip>}
							style={{ '--button-icon-top':'-2px' }}
						/>
					)}

					<CopyLink
						copy_link={main_promo_link}
						type='button'
						main={true}
						secondary={false}
						icon={false}
					/>

					<MainButton
						onClick={() => setDisplayLinkOptions(true)}
					>Share</MainButton>
				</ButtonCollection>
			</div>

			<ButtonCollection style={{ '--buttons-gap':'calc(2 * var(--grid-1))' }}>
				{(has_custom_domains_enabled && promotion.promo_type == '2') && (
					<ActionLink
						onClick={() => setManageDomain(true)}
					>Change domain</ActionLink>
				)}

				{(languages.length > 1) && (
					<ActionLink
						onClick={() => setDisplayLinkOptions(true)}
					>Multi-language links</ActionLink>
				)}

				<QRLinkCampaignLayer
					link={`${main_promo_link}`}
					promotion={promotion}
					utm_source="QR"
					languages={props.languages}
					intl={props.intl}
				/>

				<ActionLink
					_href={`/buzz/widget/${promotion.id}`}
				>Insert widget</ActionLink>

				<ActionLink
					onClick={() => setDisplayPasswordLayer(true)}
				>Password</ActionLink>
			</ButtonCollection>

			{(manage_domain) && (
				<ManageDomain
					actual_domain={domain}
					promotion_id={promotion.id}
					account_id={owner.account.id}
					organizer_id={owner.organizer_id}
					onChange={setDomain}
					close={() => setManageDomain(false)}
					intl={props.intl}
				/>
			)}

			{(display_link_options) && (
				<PromoShareLayer
					promotion={promotion}
					languages={languages}
					main_link={main_promo_link}
					backButton={() => setDisplayLinkOptions(false)}
				/>
			)}

			{(display_password_layer) && (
				<PasswordProtected
					promotion={promotion}
					backButton={() => setDisplayPasswordLayer(false)}
				/>
			)}
		</ProfileModule>
	)
};

export default injectIntl(MainPromoLink);